import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { useAuth } from '../auth';

// Set-your-own-password screen. Shown on its own (via <PasswordGate>) when `forced` — an
// admin/owner-onboarded account still on its temporary password can't reach anything else until
// this is done. Also linked from the account menu for a voluntary change.
export default function ChangePassword({ forced = false }) {
  const { changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.newPassword !== form.confirm) { setError('The new passwords do not match.'); return; }
    if (!/^(?=.*[A-Z])(?=.*[0-9]).{8,}$/.test(form.newPassword)) {
      setError('Password must be at least 8 characters and contain 1 uppercase letter and 1 number.');
      return;
    }
    setBusy(true);
    try {
      await changePassword(form.currentPassword, form.newPassword);
      navigate(forced ? '/' : '/account', { replace: true });
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to change your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mx-auto mt-4" style={{ maxWidth: 440 }}>
      <Card.Body>
        <Card.Title as="h3" className="mb-2">Set your password</Card.Title>
        {forced && (
          <Alert variant="info" className="small">
            Your account was set up with a temporary password. Choose your own password to continue.
          </Alert>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={submit}>
          <Form.Group className="mb-2">
            <Form.Label>{forced ? 'Temporary password' : 'Current password'}</Form.Label>
            <Form.Control type="password" required value={form.currentPassword} onChange={set('currentPassword')} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>New password</Form.Label>
            <Form.Control type="password" required value={form.newPassword} onChange={set('newPassword')} />
            <Form.Text muted>At least 8 characters, with 1 uppercase letter and 1 number.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Confirm new password</Form.Label>
            <Form.Control type="password" required value={form.confirm} onChange={set('confirm')} />
          </Form.Group>
          <Button type="submit" disabled={busy} className="w-100">
            {busy ? 'Saving…' : 'Save password'}
          </Button>
        </Form>
        {forced && (
          <Button variant="link" className="w-100 mt-2 text-muted" onClick={() => { logout(); navigate('/login'); }}>
            Log out
          </Button>
        )}
      </Card.Body>
    </Card>
  );
}
